/**
 * The three steps of a case as a numbered strip: Read the mail, Check the draft, Do the
 * one action (STEP_NAMES, verbatim). The quick start, module 1 and the manager page show
 * it. With `current`, the earlier steps are complete and the later ones upcoming; without
 * it, the three steps are equal.
 */

import { Badge, Timeline, type TimelineItem } from "@norican/react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { STEP_NAMES } from "@/lib/sales-flow";

import { Figure } from "./figure";

export const THREE_STEPS_TITLE = "The three steps of a case";

export type StepKey = keyof typeof STEP_NAMES;

const ORDER: readonly StepKey[] = ["read", "check", "act"];

/** The DS root exports the item type but not its status union. */
type TimelineStatus = NonNullable<TimelineItem["status"]>;

/** Where each step sits on the case page, one sentence each. */
export const STEP_PLACES: Record<StepKey, string> = {
  read: "The mail card at the top.",
  check: "The table in the middle.",
  act: "The big button in the bar.",
};

function statusOf(step: StepKey, current: StepKey | undefined): TimelineStatus | undefined {
  if (!current) {
    return undefined;
  }
  const at = ORDER.indexOf(step);
  const now = ORDER.indexOf(current);
  return at < now ? "complete" : at === now ? "current" : "upcoming";
}

export interface ThreeStepsProps {
  /** The step a lesson teaches; omit it for three equal steps. */
  current?: StepKey;
  caption?: string;
  /** Overrides the media query for the render tests. Omit it in the app. */
  orientation?: "horizontal" | "vertical";
}

export function ThreeSteps({ current, caption, orientation }: ThreeStepsProps) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const items: TimelineItem[] = ORDER.map((step, index) => ({
    id: step,
    title: STEP_NAMES[step],
    description: STEP_PLACES[step],
    status: statusOf(step, current),
    // DS-GAP(timeline-steps): the DS has no Steps component and the Timeline has no
    // numbered marker, so a subtle Badge with the number stands in as the marker.
    icon: (
      <Badge tone="accent" variant="subtle" size="sm" className="tabular-nums">
        {index + 1}
      </Badge>
    ),
  }));
  return (
    <Figure name="three-steps" title={THREE_STEPS_TITLE} caption={caption}>
      <Timeline
        orientation={orientation ?? (narrow ? "vertical" : "horizontal")}
        connector="arrow"
        items={items}
        aria-label={THREE_STEPS_TITLE}
      />
    </Figure>
  );
}
